import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useTrainingMode } from 'Src/feature/training-mode/useTrainingMode';

import { useOnErrorShowToast } from '../toasts/useOnErrorShowToast';

import { useCashBank } from './useCashBank';

import styles from './CashBankEditStartingAmount.module.scss';

const { Form, Field } = getTypedFormComponents<FormModel>();

class FormModel {
  cashBankId: number;
  amount: number;
  notes?: string;

  constructor(part?: Partial<FormModel>) {
    if (part) Object.assign(this, part);
  }
}

const CashBankEditStartingAmount: FC = () => {
  const { t } = useTranslation();
  const handleFormSubmitError = useOnErrorShowToast({ title: t('cashBank.errors.adjustOpenAmount') });
  const { data: currentCashBank } = useCashBank.query.current();
  const { mutate } = useCashBank.mutate.adjustedOpenAmount(handleFormSubmitError);
  const trainingMode = useTrainingMode();

  const form = useForm<FormModel>({
    defaultValues: {
      ...new FormModel({ cashBankId: currentCashBank?.id, amount: currentCashBank?.openAmount }),
    },
    schema: object().shape({
      amount: number().label(t('cashBank.labels.startingAmount')).required(),
    }),
  });

  const { amount } = form.state.watch();

  const modal = useModalControl({
    onOpen: () => {
      form.action.reset({
        ...new FormModel({
          cashBankId: currentCashBank?.id,
          amount: currentCashBank?.openAmount,
        })
      });
    }
  });

  const onSave = async () => {
    await form.action.submitIfValid(async ({ values }) => {
      modal.close();
      mutate(values);
    });
  };

  return (
    <>
      <CTA onClick={modal.open} label={t('cashBank.labels.adjustStartingAmount')} disabled={trainingMode.isTraining} />
      <Modal
        data-testid="cash-bank-starting-amount-modal"
        open={modal.isOpen}
        onBackDropClick={modal.close}
      >
        <ModalHeader closeButton={<CloseIconButton onClick={modal.close} />}>
          <ModalTitle copy={t('cashBank.labels.adjustStartingAmount')} />
        </ModalHeader>

        <ModalContent>
          <Form usedForm={form} omitChangeTracking>
            <Field
              type="currency"
              name="amount"
              label={t('cashBank.labels.inDollarAmount')}
            />
            <Field
              type="text"
              name="notes"
              label={t('labels.notes')}
            />
            <Flex horizontal gap="xs">
              <Copy value={t('cashBank.labels.currentStartingAmount')} />
              <Copy type="currency" value={currentCashBank?.openAmount} />
            </Flex>
          </Form>
        </ModalContent>

        <ModalActions>
          <ModalAction onClick={() => void onSave()} color="primary">
            <Flex horizontal gap="xs" className={styles.noWrap}>
              <Copy variant="button-label" value={t('cashBank.labels.adjustingTheStartingAmount')} />
              <Copy variant="button-label" type="currency" value={amount} />
            </Flex>
          </ModalAction>
        </ModalActions>
      </Modal>
    </>
  );
};

export default CashBankEditStartingAmount;
